import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { CreateChancel, Loader, NoYet } from 'components';
import { Images, useModal } from 'utils';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { documentVersionActions } from 'store';
import { formService } from '../../../../store/form/form.service';

export const AddFromLibrary = ({ formList, setFormList }) => {
    const dispatch = useDispatch();
    const [forms, setForms] = useState([]);
    const { close } = useModal();
    const [selectedSections, setSelectedSections] = useState([]);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loader, setLoader] = useState(false);
    const [loadCreate, setLoadCreate] = useState(false);

    const allSections = useMemo(() => {
        if (!forms) return [];
        return forms.flatMap((form) =>
            (form?.sections || []).map((section) => ({
                ...section,
                _formId: form.formId,
                _formTitle: form.title,
            }))
        );
    }, [forms]);

    const filteredSections = allSections?.filter((section) => section?.title?.toLowerCase()?.includes(searchTerm.toLowerCase().trim()));

    useEffect(() => {
        const fetchForms = async () => {
            setLoader(true);
            try {
                const res = await formService.getFormWidthSections({ model: formList?.model });
                setForms(res.data);
            } catch (e) {
                console.error(e);
            } finally {
                setLoader(false);
            }
        };
        fetchForms();
    }, []);

    const handleChange = (section) => {
        setSelectedSections((prev) => {
            const exists = prev.some((s) => s.id === section.id);
            if (exists) {
                return prev.filter((s) => s.id !== section.id);
            }
            return [...prev, section];
        });
        if (error) setError('');
    };

    const handleCreate = async () => {
        if (selectedSections.length === 0) {
            setError('Please select at least one section.');
            return;
        }

        setLoadCreate(true);
        try {
            const selectedList = {};
            selectedSections.forEach((s) => {
                if (!selectedList[s._formId]) {
                    selectedList[s._formId] = [];
                }
                selectedList[s._formId].push(s.sectionId);
            });
            const res = await formService.openSectionList({ selectedList });
            const fullSections = res.data;

            if (Array.isArray(fullSections)) {
                const base = formList?.sections?.length || 0;
                const newFormList = { ...formList };
                newFormList.sections = [...(newFormList.sections || []), ...fullSections];
                setFormList(newFormList);

                // Capture each added section (with its pre-filled attributes) into the
                // open draft, so the backend can reconstruct it. Without this, adding a
                // further attribute to a library section that isn't yet saved references
                // a section the reconstruction never learns about — it gets dropped and
                // the section auto-deletes. Gated on a persisted form, like every other
                // section/attribute capture.
                if (formList?.id) {
                    fullSections.forEach((section, i) => {
                        if (!section?.id) return;
                        dispatch(
                            documentVersionActions.captureDocumentChanges({
                                resourceModel: 'FORM',
                                resourceId: formList.id,
                                changes: [
                                    {
                                        changeType: 'ADD',
                                        target: {
                                            entity: 'SECTION',
                                            sectionId: section.id,
                                            label: `${base + i + 1}.${section.name || section.title || ''}`,
                                        },
                                        after: section,
                                        toIndex: base + i,
                                    },
                                ],
                            }),
                        );
                    });
                }
            }
            close();
        } catch (e) {
            console.error(e);
        } finally {
            setLoadCreate(false);
        }
    };

    return (
        <div style={{ width: 560 }}>
            <div className="modal-section-padding" style={{ borderBottom: '1px solid #DFE1E6' }}>
                <p className="modal-header-title">Add from Library</p>
                <p className="modal-box-title" style={{ color: '#475467', fontWeight: 400 }}>
                    Select saved sections to add to your form.
                </p>

                <div className="data-field-search-box" style={{ background: 'transparent' }}>
                    <img src={Images.search} alt="" />
                    <input
                        type="text"
                        placeholder="Search sections..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="modal-section-padding">
                <FormGroup className="section-from-library-wrapper">
                    {loader ? (
                        <Loader height={'300px'} />
                    ) : !filteredSections?.length ? (
                        <NoYet text="No Section Yet" />
                    ) : (
                        filteredSections?.map((opt) => (
                            <FormControlLabel
                                className="add-section-from-library-option"
                                key={`${opt._formId}-${opt.id}`}
                                control={
                                    <Checkbox
                                        checked={selectedSections.some((s) => s.id === opt.id)}
                                        onChange={() => handleChange(opt)}
                                        color="primary"
                                        style={{ padding: '0 8px 0 0' }}
                                    />
                                }
                                label={`${opt._formTitle} (${opt.title})`}
                            />
                        ))
                    )}
                </FormGroup>
            </div>

            <div className="modal-section-padding" style={{ borderTop: '1px solid #DFE1E6' }}>
                <CreateChancel
                    disabled={!selectedSections?.length || loadCreate}
                    loader={loadCreate}
                    create={'Save'}
                    chancel={'Cancel'}
                    onCreate={handleCreate}
                    onClose={close}
                    buttonWidth="100%"
                />
            </div>
        </div>
    );
};
